import React, { useCallback, useEffect, useState } from 'react';
import {
  runnerApi, errorData, errorText,
  type ApprovalAction, type ApprovalRequest, type ApprovalRequired,
} from '../../../api/migrationRunner';
import { Alert, Btn, Dialog, ErrorBox, Pill, Spinner, when } from './ui';

/**
 * Second approval (approved mockup "Database redesign", Second approval).
 *
 * A guarded action (restore over live, console write, an all-company batch)
 * answers 428 APPROVAL_REQUIRED with the exact payload. This dialog asks
 * another owner (with a reason), waits for their decision, then repeats the
 * action with the approved id. The decision itself happens on the Safety tab.
 *
 *   const gate = useApprovalGate();
 *   await gate.guard((approvalId) => runnerApi.restore(id, 'replace', confirm, approvalId));
 *   …
 *   {gate.element}
 */

/** Same idea as the server's canonicalJson: key order doesn't matter. */
function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o).sort().map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`).join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

interface Pending {
  need: ApprovalRequired;
  retry: (approvalId: string) => Promise<unknown>;
  resolve: (ok: boolean) => void;
}

export function useApprovalGate() {
  const [pending, setPending] = useState<Pending | null>(null);

  /**
   * Run `attempt()`. On 428 APPROVAL_REQUIRED open the dialog; resolves true
   * once the action went ahead (now or after approval), false if the person
   * closed the dialog. Any other error is thrown as usual.
   */
  const guard = useCallback(async (attempt: (approvalId?: string) => Promise<unknown>): Promise<boolean> => {
    try {
      await attempt();
      return true;
    } catch (err) {
      const d = errorData<ApprovalRequired>(err);
      if (d?.code !== 'APPROVAL_REQUIRED') throw err;
      return new Promise<boolean>((resolve) => setPending({ need: d as ApprovalRequired, retry: attempt, resolve }));
    }
  }, []);

  const element = pending ? (
    <ApprovalDialog
      need={pending.need}
      retry={pending.retry}
      onDone={(ok) => { pending.resolve(ok); setPending(null); }}
    />
  ) : null;

  return { guard, element };
}

const STATUS_TONE: Record<string, 'ok' | 'bad' | 'warn' | 'mut' | 'info'> = {
  pending: 'warn', approved: 'ok', declined: 'bad', expired: 'mut', used: 'mut', cancelled: 'mut',
};

function minutesLeft(iso: string): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60_000));
}

const ApprovalDialog: React.FC<{
  need: ApprovalRequired;
  retry: (approvalId: string) => Promise<unknown>;
  onDone: (ok: boolean) => void;
}> = ({ need, retry, onDone }) => {
  const [request, setRequest] = useState<ApprovalRequest | null>(null);
  const [reason, setReason] = useState('');
  const [owners, setOwners] = useState<{ email: string; name: string | null }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // An open request of mine for this exact action + payload? Pick it up.
  useEffect(() => {
    let off = false;
    runnerApi.approvals('open')
      .then(({ approvals, me }) => {
        if (off) return;
        const mine = approvals.find((a) => a.requestedBy.toLowerCase() === me.toLowerCase()
          && a.action === need.action && canonical(a.payload) === canonical(need.payload));
        if (mine) setRequest(mine);
      })
      .catch(() => { /* asking fresh still works */ })
      .finally(() => { if (!off) setChecking(false); });
    runnerApi.safety().then((s) => { if (!off) setOwners(s.otherOwners); }).catch(() => {});
    return () => { off = true; };
  }, [need]);

  // Wait for the other owner.
  useEffect(() => {
    if (!request || request.status !== 'pending') return;
    const t = setInterval(() => {
      runnerApi.approval(request.id).then(setRequest).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [request]);

  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await runnerApi.askApproval({ action: need.action as ApprovalAction, payload: need.payload, reason: reason.trim(), summary: need.summary });
      setRequest(r.approval);
      setOwners(r.otherOwners);
    } catch (err) {
      setError(errorText(err, 'Could not ask for approval'));
    } finally {
      setBusy(false);
    }
  };

  const goAhead = async () => {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      await retry(request.id);
      onDone(true);
    } catch (err) {
      setError(errorText(err, 'The action failed'));
      runnerApi.approval(request.id).then(setRequest).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!request) return;
    setBusy(true);
    try { setRequest(await runnerApi.cancelApproval(request.id)); } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };

  const done = request && ['declined', 'expired', 'used', 'cancelled'].includes(request.status);

  return (
    <Dialog
      open
      onClose={() => onDone(false)}
      title="Another owner has to approve this"
      footer={
        <>
          {request?.status === 'pending' && <Btn kind="ghost" onClick={withdraw} disabled={busy}>Withdraw</Btn>}
          <Btn kind="ghost" onClick={() => onDone(false)}>{request?.status === 'pending' ? 'Close (keep waiting)' : 'Close'}</Btn>
          {!request && <Btn onClick={ask} disabled={busy || checking || reason.trim().length < 3}>{busy ? 'Asking…' : 'Ask another owner'}</Btn>}
          {done && <Btn onClick={() => { setRequest(null); setReason(''); }}>Ask again</Btn>}
          {request?.status === 'approved' && <Btn kind="success" onClick={goAhead} disabled={busy}>{busy ? 'Working…' : 'Go ahead now'}</Btn>}
        </>
      }
    >
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <b className="block text-slate-900">{need.summary || need.message}</b>
        <span className="text-xs text-slate-500">{need.message}</span>
      </div>

      {checking && <div className="flex items-center gap-2 text-slate-500"><Spinner /> Checking for a request you already made…</div>}

      {!checking && !request && (
        <>
          <label className="block">
            <span className="mb-1 block text-[13px] font-semibold text-slate-700">Why? (the other owner sees this)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="e.g. Bad data import overwrote time cards"
            />
          </label>
          {owners && owners.length === 0 && (
            <Alert tone="warn" title="No other owner with database access">
              Nobody else can approve this. Add another owner, or change the rule on the Safety tab.
            </Alert>
          )}
          {owners && owners.length > 0 && (
            <p className="text-xs text-slate-500">Can approve: {owners.map((o) => o.name || o.email).join(', ')} · on the Safety tab of this page. Good for one hour, once.</p>
          )}
        </>
      )}

      {request && (
        <div className="space-y-2 rounded-xl border border-slate-200 p-3 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={STATUS_TONE[request.status] ?? 'mut'}>{request.status === 'pending' ? 'waiting for another owner' : request.status}</Pill>
            <span className="text-slate-500">asked {when(request.createdAt)} · reason: "{request.reason}"</span>
          </div>
          {request.status === 'pending' && (
            <div className="flex items-center gap-2 text-slate-500"><Spinner /> Expires in {minutesLeft(request.expiresAt)} min. You can close this and come back: the request stays.</div>
          )}
          {request.status === 'approved' && (
            <div className="text-green-700">Approved by <b>{request.approvedBy}</b>{request.decisionNote ? ` · "${request.decisionNote}"` : ''}. Expires in {minutesLeft(request.expiresAt)} min.</div>
          )}
          {request.status === 'declined' && (
            <div className="text-red-700">Declined by <b>{request.approvedBy}</b>{request.decisionNote ? `: "${request.decisionNote}"` : ''}.</div>
          )}
        </div>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
    </Dialog>
  );
};

export default ApprovalDialog;
